import React from 'react';

import moment from 'moment';
import { bool, func, shape } from 'prop-types';

import { DatePickerMode } from '@philips-emr/tws-components-api';
import { date, oneOf } from '@philips-emr/tws-core/lib/component/prop-types';
import { classNames, trigger } from '@philips-emr/tws-utils';

import { getCalendarTitleFormat, getGranularity } from '../date-picker-util';

function isSameDate(dateA, dateB, granularity = 'date') {
  return !!dateA && !!dateB && moment(dateA).isSame(dateB, granularity);
}

function isCurrent(date) {
  return isSameDate(moment().utc(), date);
}

function hasEvents(events = []) {
  return Boolean(events.length);
}

function isSelected(cellDate, currentDate, mode) {
  return isSameDate(currentDate, cellDate, getGranularity(mode));
}

function verifyIntervalDates(interval, copiedDate) {
  const date = moment(copiedDate);
  return (!interval.startDate || date.isSameOrAfter(interval.startDate, 'day'))
    && (!interval.endDate || date.isSameOrBefore(interval.endDate, 'day'));
}

function getClassName(props) {
  const {
    currentDate,
    endDate,
    interval,
    item,
    loading,
    mode,
    rangeEnabled,
    ranges = [],
    startDate,
  } = props;

  const { active, date: cellDate, isDateInInterval, events = [] } = item;

  const isInRange = ranges.some(range => verifyIntervalDates(range, cellDate));
  const statusRange = !rangeEnabled === isInRange;

  const isFirstLimit = isSameDate(cellDate, startDate);
  const isLastLimit = isSameDate(cellDate, endDate || startDate);
  const isLimit = isFirstLimit || isLastLimit;
  const isHidden = interval && !active;

  const selected = interval
    ? isDateInInterval || isLimit
    : isSelected(cellDate, currentDate, mode);

  return classNames({
    'c-date-picker__item': true,
    'has-events': hasEvents(events) && active,
    'is-current': isCurrent(cellDate) && !loading && !statusRange,
    'is-disabled': !active || loading || statusRange,
    'is-blackout': statusRange,
    'is-first-limit': isFirstLimit,
    'is-last-limit': isLastLimit,
    'is-limit': isLimit,
    'is-selected': selected && active && !statusRange && !loading,
    'u-hidden': isHidden,
  });
}

function DatePickerCell(props) {
  const { mode, item, onClick } = props;

  const text = getCalendarTitleFormat(mode, item.date);
  const cellClassName = getClassName(props);

  return (
    <div className={cellClassName} onClick={trigger(onClick, item)}>
      <span>{text}</span>
    </div>
  );
}

DatePickerCell.propTypes = {
  currentDate: date,
  endDate: date,
  interval: bool,
  rangeEnabled: bool,
  item: shape({
    active: bool,
    date: date,
    isDateInInterval: bool,
  }),
  loading: bool,
  mode: oneOf(Object.values(DatePickerMode)),
  onClick: func,
  startDate: date,
};

DatePickerCell.defaultProps = {
  interval: false,
  mode : DatePickerMode.MONTHLY,
};

export default DatePickerCell;
